import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { IFilePath, TAttachment } from '.';
import { getUrlPath, isFileUrlExpired, TFetchFiles, withReloadedFiles } from './utils';

const reloadedFiles = new Map<string, IFilePath>();

export default function useReloadedFiles<T extends TAttachment>(
  files: T[],
  fetchFiles?: TFetchFiles
) {
  const [reloadCount, setReloadCount] = useState(0);

  const currentFiles = useMemo(() => withReloadedFiles(files, reloadedFiles), [files, reloadCount]);

  const reloadFile = useCallback(
    async (index: number): Promise<T | undefined> => {
      const file = currentFiles[index];
      if (!fetchFiles || !file || file instanceof File || !isFileUrlExpired(file)) return file;

      const freshFiles = await fetchFiles();
      const path = getUrlPath(file);
      if (!freshFiles?.some((fresh) => getUrlPath(fresh) === path)) {
        toast.error('Could not open the file. Please reload the page.', { toastId: 'file-reload' });
        return undefined;
      }

      freshFiles.forEach((fresh) => reloadedFiles.set(getUrlPath(fresh), fresh));
      setReloadCount((count) => count + 1);
      return withReloadedFiles([file], reloadedFiles)[0];
    },
    [currentFiles, fetchFiles]
  );

  return { files: currentFiles, reloadFile };
}
